import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersRibFormData,
  ParametersRibSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { Button } from "@/components/ui/button";
import { useGetRibs } from "@/api/rib.api";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Modal } from "@/components/ui/customs/modal";

type UpdateRibModalProps = {
  closeModal: () => void;
  ribId: string;
  title: string;
};

export const UpdateRibModal = ({ closeModal, ribId, title }: UpdateRibModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { data: ribs } = useGetRibs();

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const ribForm = useForm<ParametersRibFormData>({
    resolver: zodResolver(ParametersRibSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      ribId: ribId,
    },
  });

  const onSubmit: SubmitHandler<ParametersRibFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const handleReset = async () => {
    const defaultRib = ribs?.find((r) => r.isDefault);
    if (!defaultRib) return;
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: {
        ribId: defaultRib.id,
      },
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="ribForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("ribModal.submit")}
        </Button>
      }
    >
      <form
        id="ribForm"
        onSubmit={ribForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledSelectV2
          control={ribForm.control}
          name="ribId"
          options={
            ribs?.map((r) => ({
              label: r.shortenName,
              value: r.id,
            })) || []
          }
          defaultValue={ribId}
          contentClassName="w-full"
          placeholder={t("ribModal.ribPlaceholder")}
        />
        <div className="flex items-center gap-2">
          <hr className="w-full" />
          <p className="text-sm text-gray-500">{t("ribModal.or")}</p>
          <hr className="w-full" />
        </div>
        <Button type="button" variant="ghost" onClick={handleReset} disabled={isPending}>
          {isPending ? <StyledSpinner /> : t("ribModal.reset")}
        </Button>
      </form>
    </Modal>
  );
};
